import { useCallback } from "react";
import { toast } from "sonner";
import { canNest } from "../lib/archModel";

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "resource";

/**
 * Clicking a node on the canvas is modal — what it does depends on what the page
 * is in the middle of. This hook is that router, kept in one place so the three
 * modes stay visibly mutually exclusive:
 *
 *   1. peer-connect mode  -> the click picks the connection's target
 *   2. pending placement  -> the click chooses a parent for the queued resource
 *   3. otherwise          -> the click opens the node detail modal
 *
 * Placement re-checks canNest before dispatching. The reducer enforces it too,
 * but a silent no-op there would look like a broken click, so the check here is
 * what produces the explanatory toast.
 *
 * Also owns focusNode, which has to expand collapsed ancestors before centring —
 * focusing a node inside a collapsed branch would otherwise scroll to nothing.
 */
export function useNodeInteractions({
  arch,
  dispatch,
  peerConnectMode,
  setPeerConnectMode,
  pendingPlacement,
  setPendingPlacement,
  setPeerLabelTarget,
  setCollapsedIds,
  setSelectedId,
  setModalOpen,
  setFocusTarget,
}) {
  const toggleCollapse = useCallback(
    (id) => {
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [setCollapsedIds]
  );

  const focusNode = useCallback(
    (id) => {
      if (!arch) return;
      const byId = new Map(arch.nodes.map((n) => [n.id, n]));
      const ancestors = [];
      let cur = byId.get(id)?.parentId;
      while (cur) {
        ancestors.push(cur);
        cur = byId.get(cur)?.parentId;
      }
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((a) => next.delete(a));
        return next;
      });
      setSelectedId(id);
      setModalOpen(true);
      setFocusTarget(id);
    },
    [arch, setCollapsedIds, setSelectedId, setModalOpen, setFocusTarget]
  );

  const handleNodeClick = useCallback(
    (id) => {
      // Peer-connect mode: pick this node as the target (or the source).
      if (peerConnectMode?.sourceId) {
        if (peerConnectMode.sourceId === id) return; // can't connect to self
        setPeerLabelTarget({ sourceId: peerConnectMode.sourceId, targetId: id });
        setPeerConnectMode(null);
        return;
      }
      // Pending placement: place the resource here if valid.
      if (pendingPlacement) {
        const target = arch?.nodes.find((n) => n.id === id);
        if (!target) return;
        const valid = canNest(
          arch.provider,
          pendingPlacement.tier || "compute",
          target.type,
          "resource"
        );
        if (!valid) {
          toast.error("Can't place here", {
            description: `${pendingPlacement.name} isn't valid under a ${target.type}.`,
          });
          return;
        }
        dispatch({
          type: "ADD_RESOURCE",
          parentId: id,
          name: pendingPlacement.name,
          nodeType: slugify(pendingPlacement.name),
          tier: pendingPlacement.tier,
          description: pendingPlacement.description || "",
          reasoning: "",
        });
        toast.success("Resource added", {
          description: `${pendingPlacement.name} placed under ${target.name}.`,
        });
        setPendingPlacement(null);
        return;
      }
      // Default: open the detail modal.
      setSelectedId(id);
      setModalOpen(true);
    },
    [
      arch,
      dispatch,
      peerConnectMode,
      setPeerConnectMode,
      pendingPlacement,
      setPendingPlacement,
      setPeerLabelTarget,
      setSelectedId,
      setModalOpen,
    ]
  );

  return { toggleCollapse, focusNode, handleNodeClick };
}
